import React, { useState } from 'react';

import { Alert, Button, Form, FormField, Input, Select, SpaceBetween } from '@awsui/components-react';
import { SFNClient, StartExecutionCommand } from '@aws-sdk/client-sfn';
import useAppContext from '../../contexts/app';
import awsExports from '../../aws-exports';
import useSettingsContext from '../../contexts/settings';
import VPStatusPanel from './VPStatusPanel';

const DEFAULT_MEETING_PLATFORM = { label: 'Zoom', disabled: false, value: 'Zoom' };

const MeetingForm = () => {
  const { user } = useAppContext();
  const { settings } = useSettingsContext();
  const [meetingPlatform, setMeetingPlatform] = useState(DEFAULT_MEETING_PLATFORM);
  const [meetingId, setMeetingId] = useState('');
  const [meetingPassword, setMeetingPassword] = useState('');
  const [meetingName, setMeetingName] = useState('');
  const { currentCredentials } = useAppContext();
  const [activeVpId, setActiveVpId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const meetingPlatforms = [
    { label: 'Amazon Chime', disabled: false, value: 'Chime' },
    { label: 'Zoom', disabled: false, value: 'Zoom' },
    { label: 'Microsoft Teams', disabled: true, value: 'Teams' },
    { label: 'Google Meet', disabled: true, value: 'Meet' },
  ];

  const submitMeetingForm = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    const vpId = crypto.randomUUID().slice(0, 8);
    const userName = user?.attributes?.email || 'Unknown';

    const sfnClient = new SFNClient({
      region: awsExports.aws_project_region,
      credentials: currentCredentials,
    });

    const sfnParams = {
      stateMachineArn: settings.LMAVirtualParticipantSchedulerStateMachine,
      name: `vp-${vpId}-${Date.now()}`,
      input: JSON.stringify({
        apiInfo: { httpMethod: 'POST' },
        data: {
          meetingPlatform: meetingPlatform.value,
          meetingID: meetingId.replace(/ /g, ''),
          meetingPassword,
          meetingName,
          // This will be updated when support for scheduled meetings comes, keeping it empty to maintain the contract.
          meetingTime: '',
          userName,
          vpId,
          accessToken: user.signInUserSession.accessToken.jwtToken,
          idToken: user.signInUserSession.idToken.jwtToken,
          rereshToken: user.signInUserSession.refreshToken.token,
        },
      }),
    };

    try {
      await sfnClient.send(new StartExecutionCommand(sfnParams));
      setActiveVpId(vpId);
      setMeetingId('');
      setMeetingPassword('');
      setMeetingName('');
    } catch (error) {
      console.error('Error starting meeting assistant:', error);
      setSubmitError(error.message || 'Failed to start meeting assistant');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (activeVpId) {
    return <VPStatusPanel vpId={activeVpId} onDismiss={() => setActiveVpId(null)} />;
  }

  return (
    <form
      id="meetingForm"
      onSubmit={(e) => {
        e.preventDefault();
        submitMeetingForm();
      }}
    >
      <Form variant="embedded">
        <SpaceBetween direction="vertical" size="l">
          {submitError && (
            <Alert type="error" dismissible onDismiss={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}
          <FormField label="Meeting Name">
            <Input
              onChange={({ detail }) => setMeetingName(detail.value.replace(/[/?#%+&]/g, '|'))}
              value={meetingName}
            />
          </FormField>
          <FormField label="Meeting Platform">
            <Select
              onChange={({ detail }) => setMeetingPlatform(detail.selectedOption)}
              options={meetingPlatforms}
              selectedOption={meetingPlatform}
            />
          </FormField>

          <FormField label="Meeting ID">
            <Input onChange={({ detail }) => setMeetingId(detail.value)} value={meetingId} />
          </FormField>

          <FormField label="Meeting Password (if applicable)">
            <Input
              onChange={({ detail }) => setMeetingPassword(detail.value)}
              value={meetingPassword}
              type="password"
            />
          </FormField>

          <FormField>
            <SpaceBetween direction="vertical" size="l">
              <Button
                variant="primary"
                form="meetingForm"
                disabled={!meetingId || !meetingName || isSubmitting}
                loading={isSubmitting}
              >
                Join Now
              </Button>
            </SpaceBetween>
          </FormField>
        </SpaceBetween>
      </Form>
    </form>
  );
};
export default MeetingForm;
